import { Cake, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { daysUntilBirthday, formatBirthdayLabel } from "@/lib/birthday";
import { useSettings } from "@/contexts/SettingsContext";

export function BirthdaySection({ client, onIssued, testid }) {
  const { settings } = useSettings();
  const [optOut, setOptOut] = useState(!!client?.birthday_voucher_opt_out);

  useEffect(() => { setOptOut(!!client?.birthday_voucher_opt_out); }, [client?.id, client?.birthday_voucher_opt_out]);

  if (!client?.dob) return null;
  const days = daysUntilBirthday(client.dob);
  if (days === null) return null;
  const isSoon = days <= 7;

  const send = async () => {
    try {
      const r = await api.post(`/clients/${client.id}/birthday-voucher`, {
        amount: settings.birthday_voucher_amount || 500,
      });
      const code = r?.data?.data?.gift_card?.code;
      const amt = r?.data?.data?.gift_card?.balance;
      toast.success("Birthday voucher sent ✨", {
        description: `${code} (₹${amt}) sent to ${client.name} on WhatsApp`,
      });
      onIssued?.(r?.data?.data);
    } catch {
      toast.error("Couldn't send voucher");
    }
  };

  const toggleOptOut = async () => {
    const next = !optOut;
    setOptOut(next);
    try {
      await api.patch(`/clients/${client.id}`, { birthday_voucher_opt_out: next });
      toast.success(next ? "Auto birthday voucher disabled" : "Auto birthday voucher enabled");
    } catch {
      setOptOut(!next);
      toast.error("Couldn't update preference");
    }
  };

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: isSoon && !optOut ? "linear-gradient(180deg,rgba(232,201,138,0.22),rgba(212,168,83,0.10))" : "rgba(255,255,255,0.55)",
        border: `1px solid ${isSoon && !optOut ? "rgba(212,168,83,0.45)" : "rgba(255,255,255,0.80)"}`,
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: isSoon && !optOut ? "rgba(212,168,83,0.20)" : "rgba(124,92,122,0.12)",
              animation: days === 0 && !optOut ? "pulseRing 1.6s ease-out infinite" : undefined,
            }}
          >
            <Cake size={18} style={{ color: isSoon && !optOut ? "#8E6A1A" : "#5E4060" }} />
          </div>
          <div>
            <div className="overline mb-0.5" style={{ color: isSoon && !optOut ? "#8E6A1A" : undefined }}>
              {isSoon && !optOut ? (days === 0 ? "Birthday today!" : "Birthday soon") : "Birthday"}
            </div>
            <div className="font-sora font-semibold text-[14px] text-[var(--color-text-primary)]">
              {new Date(client.dob).toLocaleDateString([], { month: "long", day: "numeric" })}
              <span className="font-inter font-normal text-[12px] text-[var(--color-text-muted)] ml-2">· {formatBirthdayLabel(days)} away</span>
            </div>
          </div>
        </div>
        {isSoon && !optOut && (
          <button onClick={send} className="btn-3d btn-gold-3d btn-sm" data-testid="stylist-birthday-voucher-btn">
            <Send size={13} /> Send ₹{settings.birthday_voucher_amount || 500} voucher
          </button>
        )}
      </div>

      {/* Auto birthday voucher toggle */}
      <div className="mt-3 pt-3 border-t border-white/40 flex items-center justify-between">
        <span className="font-inter text-[12.5px] text-[var(--color-text-secondary)]">Auto birthday voucher</span>
        <button
          type="button"
          onClick={toggleOptOut}
          aria-pressed={!optOut}
          data-testid={`birthday-optout-toggle-${client.id}`}
          className={`relative inline-flex items-center h-6 w-11 rounded-full transition border ${!optOut ? "bg-[var(--color-success)]/85 border-[var(--color-success-deep)]/40" : "bg-white/70 border-[var(--color-text-faint)]/50"}`}
        >
          <span className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition ${!optOut ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      {optOut && <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] mt-1.5 text-right">Opted out — no auto voucher will be issued</div>}
    </div>
  );
}

export default BirthdaySection;

